'use client'

/*
 * IQPercentileCalculator
 *
 * The interactive widget on /iq-percentile-calculator.
 *
 * HOW THIS IS STRUCTURED, AND WHY:
 * - All the maths lives in src/lib/iqStats.js, not here. That file is imported
 *   by the server page too, so the static reference table rendered into the
 *   HTML and the live widget can never disagree with each other.
 * - This component renders a complete, correct result on the server at its
 *   default score. The HTML that reaches a crawler is not an empty shell
 *   waiting for JavaScript - it has a real percentile, a real bell curve and
 *   real prose in it.
 * - The query string is read in an effect rather than through useSearchParams.
 *   useSearchParams would opt the whole route out of static generation unless
 *   it is wrapped in Suspense, and this page has no reason to be dynamic. The
 *   effect approach keeps the page a static file on the CDN and still supports
 *   shareable /iq-percentile-calculator?iq=130 links.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import Link from 'next/link'
import { ArrowRight, RotateCcw, Link2, Check } from 'lucide-react'
import TestPromptPopup from '@/components/TestPromptPopup'
import {
  SCALES,
  MIN_IQ,
  MAX_IQ,
  MODEL_RELIABLE_MAX,
  percentileForIQ,
  iqForPercentile,
  zScoreForIQ,
  rarityOneIn,
  formatOneIn,
  formatPercentile,
  classifyIQ,
  confidenceInterval,
  convertBetweenScales,
  peopleWhoScoreHigher,
} from '@/lib/iqStats'

// Tailwind scans source files for complete class strings, so these cannot be
// built by concatenation at runtime. Each band's palette is written out in full.
const BAND_THEMES = {
  purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', solid: 'bg-purple-600', pill: 'bg-purple-100 text-purple-800', curve: '#9333ea' },
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', solid: 'bg-blue-600', pill: 'bg-blue-100 text-blue-800', curve: '#2563eb' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', solid: 'bg-emerald-600', pill: 'bg-emerald-100 text-emerald-800', curve: '#059669' },
  slate: { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', solid: 'bg-slate-600', pill: 'bg-slate-100 text-slate-800', curve: '#475569' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', solid: 'bg-amber-600', pill: 'bg-amber-100 text-amber-800', curve: '#d97706' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', solid: 'bg-orange-600', pill: 'bg-orange-100 text-orange-800', curve: '#ea580c' },
  rose: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', solid: 'bg-rose-600', pill: 'bg-rose-100 text-rose-800', curve: '#e11d48' },
}

const DEFAULT_IQ = 120

// The curve is drawn in z-space and labelled in IQ. Doing it the other way
// round would need a different viewBox for every deviation scale.
const Z_MIN = -4
const Z_MAX = 4
const PLOT_LEFT = 24
const PLOT_RIGHT = 376
const PLOT_BASE = 150
const PLOT_HEIGHT = 124

function zToX(z) {
  const clamped = Math.max(Z_MIN, Math.min(Z_MAX, z))
  return PLOT_LEFT + ((clamped - Z_MIN) / (Z_MAX - Z_MIN)) * (PLOT_RIGHT - PLOT_LEFT)
}

function densityToY(z) {
  return PLOT_BASE - Math.exp(-(z * z) / 2) * PLOT_HEIGHT
}

// Pre-computed once: the outline is the same shape regardless of score or scale.
const CURVE_POINTS = Array.from({ length: 161 }, (_, i) => {
  const z = Z_MIN + (i / 160) * (Z_MAX - Z_MIN)
  return { z, x: zToX(z), y: densityToY(z) }
})

const CURVE_PATH = CURVE_POINTS.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ')

function shadedPath(zScore) {
  const cut = Math.max(Z_MIN, Math.min(Z_MAX, zScore))
  const pts = CURVE_POINTS.filter((p) => p.z <= cut)
  if (pts.length === 0) return ''
  const edgeY = densityToY(cut)
  const edgeX = zToX(cut)
  return [
    `M ${PLOT_LEFT} ${PLOT_BASE}`,
    ...pts.map((p) => `L ${p.x.toFixed(2)} ${p.y.toFixed(2)}`),
    `L ${edgeX.toFixed(2)} ${edgeY.toFixed(2)}`,
    `L ${edgeX.toFixed(2)} ${PLOT_BASE}`,
    'Z',
  ].join(' ')
}

function clampIQ(value) {
  if (!isFinite(value)) return DEFAULT_IQ
  return Math.max(MIN_IQ, Math.min(MAX_IQ, value))
}

export default function IQPercentileCalculator({ scoreArticles = {} }) {
  const [mode, setMode] = useState('score')
  const [scaleId, setScaleId] = useState('sd15')
  const [iq, setIq] = useState(DEFAULT_IQ)
  const [iqText, setIqText] = useState(String(DEFAULT_IQ))
  const [percentileInput, setPercentileInput] = useState('98')
  const [copied, setCopied] = useState(false)

  const scale = useMemo(() => SCALES.find((s) => s.id === scaleId) || SCALES[0], [scaleId])

  // Hydrate from a shared link, e.g. /iq-percentile-calculator?iq=132&scale=sd16
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const rawScale = params.get('scale')
    if (rawScale && SCALES.some((s) => s.id === rawScale)) setScaleId(rawScale)

    const rawIq = params.get('iq')
    if (rawIq !== null && rawIq.trim() !== '') {
      const parsed = clampIQ(parseFloat(rawIq))
      setIq(parsed)
      setIqText(String(parsed))
    }
  }, [])

  const setScore = useCallback((value) => {
    const next = clampIQ(value)
    setIq(next)
    setIqText(String(next))
  }, [])

  // In percentile mode the percentile is what the user pinned, so a scale
  // change has to move the score to match. In score mode the opposite holds:
  // the score is pinned and the percentile is free to move.
  const selectScale = (nextId) => {
    setScaleId(nextId)
    if (mode === 'percentile') {
      const nextScale = SCALES.find((s) => s.id === nextId) || SCALES[0]
      const parsed = parseFloat(percentileInput)
      if (!isNaN(parsed) && parsed > 0 && parsed < 100) {
        setScore(Math.round(iqForPercentile(parsed, nextScale.sd) * 10) / 10)
      }
    }
  }

  // Typing is deliberately not clamped on every keystroke. Clamping mid-entry
  // means someone typing "145" sees the field jump to 200 after the "1", then
  // fight them for the rest of the number.
  const handleIqText = (raw) => {
    setIqText(raw)
    const parsed = parseFloat(raw)
    if (!isNaN(parsed) && parsed >= MIN_IQ && parsed <= MAX_IQ) setIq(parsed)
  }

  const handleIqBlur = () => {
    const parsed = parseFloat(iqText)
    setScore(isNaN(parsed) ? DEFAULT_IQ : parsed)
  }

  // Switching modes carries the current result across. Without this, moving to
  // percentile mode would show whatever was last typed there, silently
  // discarding the score the user had just set.
  const switchMode = (next) => {
    if (next === 'percentile') {
      const current = percentileForIQ(iq, scale.sd)
      setPercentileInput(formatPercentile(current))
    }
    setMode(next)
  }

  const handlePercentile = (raw) => {
    setPercentileInput(raw)
    const parsed = parseFloat(raw)
    if (!isNaN(parsed) && parsed > 0 && parsed < 100) {
      setIq(clampIQ(Math.round(iqForPercentile(parsed, scale.sd) * 10) / 10))
    }
  }

  const copyLink = async () => {
    const url = `${window.location.origin}${window.location.pathname}?iq=${iq}&scale=${scaleId}`
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard access is denied in some embedded browsers. Falling back to
      // updating the address bar at least gives the user something to copy.
      window.history.replaceState(null, '', `?iq=${iq}&scale=${scaleId}`)
    }
  }

  const z = zScoreForIQ(iq, scale.sd)
  const percentile = percentileForIQ(iq, scale.sd)
  const band = classifyIQ(iq, scale.sd)
  const theme = BAND_THEMES[band.color] || BAND_THEMES.slate
  const ci = confidenceInterval(iq, scale.sd)
  const room = peopleWhoScoreHigher(iq, scale.sd)
  const wechslerEquivalent = scale.sd === 15 ? iq : convertBetweenScales(iq, scale.sd, 15)
  const roundedWechsler = Math.round(wechslerEquivalent)
  const linkedArticle = scoreArticles[roundedWechsler]
  const beyondModel = iq > MODEL_RELIABLE_MAX || iq < 55

  const ciPercentiles = {
    low: percentileForIQ(ci.low, scale.sd),
    high: percentileForIQ(ci.high, scale.sd),
  }

  return (
    <div className="bg-white border border-gray-200 rounded-3xl shadow-xl overflow-hidden">
      {/* ---------------- Controls ---------------- */}
      <div className="p-6 sm:p-8 border-b border-gray-200 bg-gray-50/70">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="inline-flex rounded-xl bg-gray-200/80 p-1" role="group" aria-label="Calculator mode">
            <button
              type="button"
              onClick={() => switchMode('score')}
              aria-pressed={mode === 'score'}
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${mode === 'score' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            >
              I know my score
            </button>
            <button
              type="button"
              onClick={() => switchMode('percentile')}
              aria-pressed={mode === 'percentile'}
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${mode === 'percentile' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            >
              I know my percentile
            </button>
          </div>

          <button
            type="button"
            onClick={() => { setScore(DEFAULT_IQ); setScaleId('sd15'); setPercentileInput('98') }}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-800 transition-colors self-start sm:self-auto"
          >
            <RotateCcw className="w-4 h-4 mr-1.5" />
            Reset
          </button>
        </div>

        {mode === 'score' ? (
          <div>
            <label htmlFor="iq-input" className="block text-sm font-semibold text-gray-700 mb-2">
              Your IQ score
            </label>
            <div className="flex items-center gap-4">
              <input
                id="iq-input"
                type="number"
                inputMode="numeric"
                min={MIN_IQ}
                max={MAX_IQ}
                value={iqText}
                onChange={(e) => handleIqText(e.target.value)}
                onBlur={handleIqBlur}
                className="w-32 text-3xl font-bold text-gray-900 bg-white border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-xl px-4 py-3 outline-none transition-all"
              />
              <input
                type="range"
                min={MIN_IQ}
                max={MAX_IQ}
                step={1}
                value={iq}
                onChange={(e) => setScore(parseFloat(e.target.value))}
                aria-label="IQ score slider"
                className="flex-1 h-2 accent-blue-600 cursor-pointer"
              />
            </div>
          </div>
        ) : (
          <div>
            <label htmlFor="pct-input" className="block text-sm font-semibold text-gray-700 mb-2">
              Your percentile rank
            </label>
            <div className="flex items-center gap-4">
              <input
                id="pct-input"
                type="number"
                inputMode="decimal"
                min="0.01"
                max="99.99"
                step="0.01"
                value={percentileInput}
                onChange={(e) => handlePercentile(e.target.value)}
                className="w-32 text-3xl font-bold text-gray-900 bg-white border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-xl px-4 py-3 outline-none transition-all"
              />
              <p className="text-sm text-gray-600 flex-1">
                Enter the percentile you were told, and this gives you the IQ
                score it corresponds to on the {scale.name}.
              </p>
            </div>
          </div>
        )}

        <fieldset className="mt-6">
          <legend className="block text-sm font-semibold text-gray-700 mb-2">
            Which scale is the score on?
          </legend>
          <div className="flex flex-wrap gap-2">
            {SCALES.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => selectScale(s.id)}
                aria-pressed={scaleId === s.id}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border-2 transition-all ${
                  scaleId === s.id
                    ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                    : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400'
                }`}
              >
                {s.label} · {s.name}
              </button>
            ))}
          </div>
          <p className="text-sm text-gray-600 mt-3 leading-relaxed">{scale.note}</p>
        </fieldset>
      </div>

      {/* ---------------- Headline result ---------------- */}
      <div className={`p-6 sm:p-8 ${theme.bg} border-b ${theme.border}`}>
        <div className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-8 items-center">
          <div>
            <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">
              Percentile rank
            </p>
            <div className="flex items-baseline gap-2 mb-3">
              <span className={`text-6xl sm:text-7xl font-bold ${theme.text} tabular-nums`}>
                {formatPercentile(percentile)}
              </span>
              <span className="text-2xl font-semibold text-gray-500">th</span>
            </div>
            <p className="text-lg text-gray-800 leading-relaxed mb-4">
              An IQ of <strong>{Number.isInteger(iq) ? iq : iq.toFixed(1)}</strong> on
              the {scale.name} is higher than{' '}
              <strong>{formatPercentile(percentile)}%</strong> of the population.
            </p>
            <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${theme.pill}`}>
              {band.label}
            </span>
            <p className="text-sm text-gray-600 mt-3 leading-relaxed">{band.blurb}</p>
          </div>

          {/* Bell curve. Rendered from the same maths as the numbers beside it. */}
          <div>
            <svg viewBox="0 0 400 180" className="w-full h-auto" role="img" aria-label={`Bell curve showing an IQ of ${iq} at the ${formatPercentile(percentile)}th percentile`}>
              <path d={shadedPath(z)} fill={theme.curve} fillOpacity="0.28" />
              <path d={CURVE_PATH} fill="none" stroke={theme.curve} strokeWidth="2.5" />
              <line x1={PLOT_LEFT} y1={PLOT_BASE} x2={PLOT_RIGHT} y2={PLOT_BASE} stroke="#cbd5e1" strokeWidth="1.5" />

              {/* Score marker */}
              <line x1={zToX(z)} y1={densityToY(Math.max(Z_MIN, Math.min(Z_MAX, z)))} x2={zToX(z)} y2={PLOT_BASE} stroke={theme.curve} strokeWidth="2.5" strokeDasharray="4,3" />
              <circle cx={zToX(z)} cy={densityToY(Math.max(Z_MIN, Math.min(Z_MAX, z)))} r="5" fill={theme.curve} />

              {/* Axis labels at each standard deviation, in the current scale. */}
              {[-3, -2, -1, 0, 1, 2, 3].map((tick) => (
                <g key={tick}>
                  <line x1={zToX(tick)} y1={PLOT_BASE} x2={zToX(tick)} y2={PLOT_BASE + 4} stroke="#cbd5e1" strokeWidth="1.5" />
                  <text x={zToX(tick)} y={PLOT_BASE + 18} textAnchor="middle" fontSize="11" fill="#64748b">
                    {Math.round(100 + tick * scale.sd)}
                  </text>
                </g>
              ))}
              <text x={zToX(z)} y={172} textAnchor="middle" fontSize="11" fontWeight="700" fill={theme.curve}>
                you
              </text>
            </svg>
          </div>
        </div>
      </div>

      {/* ---------------- Supporting figures ---------------- */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-gray-200 border-b border-gray-200">
        <div className="p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Rarity</p>
          <p className="text-xl font-bold text-gray-900">{formatOneIn(rarityOneIn(iq, scale.sd))}</p>
          <p className="text-xs text-gray-500 mt-1">
            score {z >= 0 ? 'this high or higher' : 'this low or lower'}
          </p>
        </div>
        <div className="p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Standard deviations</p>
          <p className="text-xl font-bold text-gray-900 tabular-nums">
            {z >= 0 ? '+' : ''}{z.toFixed(2)}
          </p>
          <p className="text-xs text-gray-500 mt-1">from the mean of 100</p>
        </div>
        <div className="p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Would outscore you</p>
          <p className="text-xl font-bold text-gray-900">
            {room.fewerThanOne ? 'fewer than 1' : `about ${room.count.toLocaleString('en-GB')}`}
          </p>
          <p className="text-xs text-gray-500 mt-1">in {room.label}</p>
        </div>
        <div className="p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">95% confidence band</p>
          <p className="text-xl font-bold text-gray-900 tabular-nums">
            {Math.round(ci.low)} to {Math.round(ci.high)}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {formatPercentile(ciPercentiles.low)} to {formatPercentile(ciPercentiles.high)} percentile
          </p>
        </div>
      </div>

      {/* ---------------- Scale conversion ---------------- */}
      <div className="p-6 sm:p-8 border-b border-gray-200">
        <h2 className="text-lg font-bold text-gray-900 mb-1">The same score on every scale</h2>
        <p className="text-sm text-gray-600 mb-4 leading-relaxed">
          These three numbers are not three different results. They are one
          result written three ways, which is why Mensa quotes a different entry
          figure for each.
        </p>
        <div className="grid sm:grid-cols-3 gap-3">
          {SCALES.map((s) => {
            const converted = s.sd === scale.sd ? iq : convertBetweenScales(iq, scale.sd, s.sd)
            const isCurrent = s.id === scale.id
            return (
              <div
                key={s.id}
                className={`rounded-xl border-2 p-4 transition-all ${isCurrent ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{s.label}</span>
                  {isCurrent && <span className="text-[10px] font-bold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">YOUR SCALE</span>}
                </div>
                <p className="text-3xl font-bold text-gray-900 tabular-nums">{Math.round(converted)}</p>
                <p className="text-xs text-gray-500 mt-1">{s.name}</p>
                <p className="text-xs text-gray-400 mt-2">Mensa entry: {s.mensaEntry}</p>
              </div>
            )
          })}
        </div>
      </div>

      {/* ---------------- Caveats and onward links ---------------- */}
      <div className="p-6 sm:p-8 bg-gray-50">
        {beyondModel && (
          <div className="mb-5 rounded-xl border border-amber-300 bg-amber-50 p-4">
            <p className="text-sm text-amber-900 leading-relaxed">
              <strong>A note on this score.</strong> Above about 160 and below
              about 55, the figures here are what the normal curve implies rather
              than something any test has measured. No test is normed on a sample
              large enough to place a score this rare, so treat the percentile as
              a mathematical extension of the model, not a measurement.
            </p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-3">
            <Link
              href="/test"
              className="inline-flex items-center bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold px-6 py-3 rounded-xl transition-all shadow-md hover:shadow-lg"
            >
              Find out your real score
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            {linkedArticle && (
              <Link
                href={`/articles/iq-scores/${linkedArticle.slug}`}
                className="inline-flex items-center bg-white border-2 border-gray-300 hover:border-blue-400 text-gray-800 font-semibold px-5 py-3 rounded-xl transition-all"
              >
                Is {roundedWechsler} a good IQ score?
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            )}
          </div>

          <button
            type="button"
            onClick={copyLink}
            className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-blue-700 transition-colors"
          >
            {copied ? <Check className="w-4 h-4 mr-1.5 text-emerald-600" /> : <Link2 className="w-4 h-4 mr-1.5" />}
            {copied ? 'Link copied' : 'Copy link to this result'}
          </button>
        </div>
      </div>

      {/* Sits inside the calculator purely so it can reference the score the
          reader is currently looking at. It renders as a fixed overlay, so the
          nesting has no bearing on where it appears on screen. */}
      <TestPromptPopup
        delayMs={10000}
        score={Number.isInteger(iq) ? iq : Math.round(iq)}
        percentileLabel={formatPercentile(percentile)}
      />
    </div>
  )
}
